import { makeAutoObservable, runInAction } from 'mobx'
import {
  DEFAULT_SOUND_NOTIFICATIONS,
  fetchWorkspaceSettings,
  parseWorkspaceSettings,
  patchWorkspaceSettings,
  type PatchWorkspaceSettingsPayload,
  type SoundNotificationsSettings,
  type WorkspaceSettings,
} from '@/api/workspaceSettingsApi'

export class OperatorWorkspaceSettingsStore {
  soundNotifications: SoundNotificationsSettings = {
    ...DEFAULT_SOUND_NOTIFICATIONS,
    events: { ...DEFAULT_SOUND_NOTIFICATIONS.events },
  }
  loading = false
  loadedOnce = false

  private loadPromise: Promise<void> | null = null

  constructor() {
    makeAutoObservable(this)
  }

  applySettings(settings: WorkspaceSettings) {
    this.soundNotifications = {
      enabled: settings.soundNotifications.enabled,
      events: { ...settings.soundNotifications.events },
    }
  }

  applyFromResponse(json: unknown): boolean {
    try {
      const parsed = parseWorkspaceSettings(json)
      this.applySettings(parsed)
      return true
    } catch {
      return false
    }
  }

  private async fetchAndApply(baseUrl: string, headers: Record<string, string>, markLoaded: boolean): Promise<void> {
    const base = baseUrl.replace(/\/$/, '')
    if (!base) return

    this.loading = true
    try {
      const settings = await fetchWorkspaceSettings(base, headers)
      runInAction(() => {
        this.applySettings(settings)
      })
    } catch (err) {
      console.error('[OperatorWorkspaceSettingsStore] GET workspace settings failed:', err)
    } finally {
      runInAction(() => {
        this.loading = false
        if (markLoaded) {
          this.loadedOnce = true
        }
      })
    }
  }

  async loadOnce(baseUrl: string, headers: Record<string, string>): Promise<void> {
    if (this.loadedOnce) return
    if (this.loadPromise) return this.loadPromise

    this.loadPromise = this.fetchAndApply(baseUrl, headers, true).finally(() => {
      this.loadPromise = null
    })

    return this.loadPromise
  }

  async refresh(baseUrl: string, headers: Record<string, string>): Promise<void> {
    await this.fetchAndApply(baseUrl, headers, true)
  }

  async patchSoundNotifications(
    baseUrl: string,
    headers: Record<string, string>,
    payload: PatchWorkspaceSettingsPayload['settings']['soundNotifications'],
  ): Promise<void> {
    const base = baseUrl.replace(/\/$/, '')
    if (!base) return

    const settings = await patchWorkspaceSettings(base, headers, {
      settings: { soundNotifications: payload },
    })

    runInAction(() => {
      this.applySettings(settings)
      this.loadedOnce = true
    })
  }

  reset() {
    this.soundNotifications = {
      ...DEFAULT_SOUND_NOTIFICATIONS,
      events: { ...DEFAULT_SOUND_NOTIFICATIONS.events },
    }
    this.loading = false
    this.loadedOnce = false
    this.loadPromise = null
  }
}
